/**
 * HTTPRequest util to make API calls
 * Usage:
 * const localAPI = new HTTPRequest('http://localhost:3000/api/v1/')
 * localAPI.get('/user-info').then(...)
 */
export default class HTTPRequest {
  emptyContentStatusCodes = [204, 205];
  
  constructor(baseURL = '', defaultHeaders = {}, defaultOptions = {}) {
    this.baseURL = baseURL;
    this.defaultHeaders = defaultHeaders;
    this.defaultOptions = defaultOptions;
    this.FETCH_TIMEOUT = 90000; // 90 seconds
  }
  
  /**
     * Parses the JSON returned by a network request
     * @param  {object} response A response from a network request
     * @return {object}          The parsed JSON from the request
     */
  parseJSON = response => {
    if (this.emptyContentStatusCodes.includes(response.status)) return null;
    return response.json();
  };
  
  /**
     * Checks if a network request came back fine, and throws an error if not
     * @param  {object} response   A response from a network request
     * @return {object|undefined} Returns either the response, or throws an error
     */
  checkError = async response => {
    if (response.ok) return response;
    let body;
    try {
      body = await response.json();
    } catch (ex) {
      body = response;
    }
    const error = new Error(response.statusText);
    error.response = body;
    throw error;
  };
  
  /**
     * Requests a URL, returning a promise
     * @param  {string} url       The URL we want to request
     * @param  {object} [options] The options we want to pass to "fetch"
     * @return {object}           The response data
     */
  request = (url, options) => {
    let didTimeOut = false;
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        didTimeOut = true;
        const error = new Error('Request timed out');
        error.code = 'TIMEOUT_ERROR';
        reject(error);
      }, this.FETCH_TIMEOUT);
  
      fetch(`${this.baseURL}${url}`, options)
        .then(response => {
          // Clear the timeout as cleanup
          clearTimeout(timeout);
          if (!didTimeOut) {
            resolve(response);
          }
        })
        .catch(
          // ignoring coverage as fetch exception cannot be mocked
          /* istanbul ignore next */ err => {
            // Rejection already happened with setTimeout
            if (didTimeOut) return;
            // Reject with error
            reject(err);
          },
        );
    })
      .then(this.checkError)
      .then(this.parseJSON);
  };
  
  /**
     * Requests a URL with given method and body, returning a promise
     * @param   {string}    method
     * @param   {string}    url
     * @param   {*}         body
     * @param   {boolean}   [isJSON] - whether to parse response JSON or not
     * @param   {object}    [customHeaders]
     * @param   {object}    [customOptions]
     * @return  {object}    response data
     */
  requestWithBody = (
    method,
    url,
    body,
    isJSON = true,
    customHeaders = {},
    customOptions = {},
  ) => {
    const headers = { ...this.defaultHeaders, ...customHeaders };
    const options = { ...this.defaultOptions, ...customOptions };
    const apiOptions = {
      method,
      headers,
      body: isJSON ? JSON.stringify(body) : body,
      ...options,
    };
    return this.request(url, apiOptions);
  };
  
  /**
     * Requests a URL with GET method, returning a promise
     * @param   {string} url
     * @param   {object} [customHeaders]
     * @param   {object} [customOptions]
     * @return  {object} response data
     */
  get = (url, customHeaders = {}, customOptions = {}) => {
    const headers = { ...this.defaultHeaders, ...customHeaders };
    const options = { ...this.defaultOptions, ...customOptions };
    const apiOptions = {
      method: 'GET',
      headers,
      ...options,
    };
    return this.request(url, apiOptions);
  };
  
  /**
     * Requests a URL with POST method, returning a promise
     * @param   {string}    url
     * @param   {*}         body
     * @param   {boolean}   [isJSON] - whether to parse response JSON or not
     * @param   {object}    [headers]
     * @param   {object}    [options]
     * @return  {object}    response data
     */
  post = (url, body, isJSON = true, headers = {}, options = {}) =>
    this.requestWithBody('POST', url, body, isJSON, headers, options);
  
  /**
     * Requests a URL with PUT method, returning a promise
     * @param   {string}    url
     * @param   {*}         body
     * @param   {boolean}   [isJSON] - whether to parse response JSON or not
     * @param   {object}    [headers]
     * @param   {object}    [options]
     * @return  {object}    response data
     */
  put = (url, body, isJSON = true, headers = {}, options = {}) =>
    this.requestWithBody('PUT', url, body, isJSON, headers, options);
  
  /**
     * Requests a URL with PATCH method, returning a promise
     * @param   {string}    url
     * @param   {*}         body
     * @param   {boolean}   [isJSON] - whether to parse response JSON or not
     * @param   {object}    [headers]
     * @param   {object}    [options]
     * @return  {object}    response data
     */
  patch = (url, body, isJSON = true, headers = {}, options = {}) =>
    this.requestWithBody('PATCH', url, body, isJSON, headers, options);
  
  /**
     * Requests a URL with DELETE method, returning a promise
     * @param   {string}    url
     * @param   {*}         body
     * @param   {boolean}   [isJSON] - whether to parse response JSON or not
     * @param   {object}    [headers]
     * @param   {object}    [options]
     * @return  {object}    response data
     */
  delete = (url, body, isJSON = true, headers = {}, options = {}) =>
    this.requestWithBody('DELETE', url, body, isJSON, headers, options);
  
  // save blob data as file
  saveBlob = (() => {
    const a = document.createElement('a');
    a.style.cssText = 'display: none';
    return (blob, fileName) => {
      // for download in IE alone
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    };
  })();
  
  /**
     * check if blob is JSON or not
     * @param blob
     * @returns {Promise<boolean>}
     */
  static async isJSON(blob) {
    try {
      await new Response(blob).json();
    } catch (e) {
      return false;
    }
    return true;
  }
  
  /**
     * download file using API call
     * @param url [string]
     * @param customHeaders [object]
     * @param customOptions [object]
     * @param devFileType {string} used only on dev environment
     * @param devDownloadJSON [boolean] allow JSON file download in dev environment
     * @returns {Promise<Response | never>}
     */
  download = (
    url,
    customHeaders = {},
    customOptions = {},
    devFileType = 'csv',
    devDownloadJSON = false,
  ) => {
    const headers = { ...this.defaultHeaders, ...customHeaders };
    const options = { ...this.defaultOptions, ...customOptions };
    const apiOptions = {
      method: 'GET',
      headers,
      ...options,
    };
  
    return fetch(`${this.baseURL}${url}`, apiOptions).then(async response => {
      if (response.ok) {
        let contentDisposition = response.headers.get('Content-Disposition');
        const blob = await response.blob();
        if (
          !contentDisposition &&
            window.location.hostname.indexOf('.guardianlife.com') === -1
        ) {
          // throw error if response is JSON and downloadJSON is false
          if (!devDownloadJSON && (await HTTPRequest.isJSON(blob))) {
            return HTTPRequest.throwResponseError(response);
          }
          // mock Content-Disposition header as it can't be read in development due to CORS issue
          contentDisposition = `attachment; filename=testFileDownload.${devFileType}`;
        }
        // throw error if no content disposition is available
        if (!contentDisposition)
          return HTTPRequest.throwResponseError(response);
  
        // extract filename and start download
        const contentDispositionValues = contentDisposition.split(';');
        if (contentDispositionValues.includes('attachment')) {
          const filename = contentDisposition.split('filename=')[1];
          this.saveBlob(blob, filename);
          return Promise.resolve(filename);
        }
      }
      return HTTPRequest.throwResponseError(response);
    });
  };
  
  // parse and set response json in error if exists
  static async throwResponseError(response) {
    const error = new Error('Download error');
    try {
      error.response = await response.json();
      // eslint-disable-next-line no-empty
    } catch (ex) {
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      throw error;
    }
  }
}